import { GET_SCHOOL_QUERY } from '@/graphql';
import type { School } from '@/types/composition-functions';
import { useQuery } from '@vue/apollo-composable';
import { useBranding } from '@/composables/useBranding';

/**
 * Composable for school data. Each consumer gets its own reactive instance,
 * but Apollo's cache-first policy ensures only one network request.
 * useQuery with cache-first natively deduplicates across all consumers.
 */
export const useSchoolQuery = () => {
  const {
    result: schoolResult,
    loading: loadingSchool,
    refetch: refetchSchool,
    error: errorSchool,
    onResult: onResultSchool,
  } = useQuery<{ getSchool: School }>(GET_SCHOOL_QUERY);

  const { updateBranding } = useBranding();

  watch(
    schoolResult,
    (newResult) => {
      const school = newResult?.getSchool as unknown as {
        logo?: string | null;
        settings?: { branding?: { app_name?: string; primary_color?: string; accent_color?: string; favicon_url?: string; login_bg_url?: string; hide_gradeverse_branding?: boolean } };
      } | undefined;
      const branding = school?.settings?.branding;
      // getSchool already returns the school's logo (top-level) plus the full
      // settings blob — carry ALL branding fields through, not just four, so an
      // authenticated user on the platform host gets their logo/accent/login-bg
      // instead of falling back to the default GradeVerse chrome.
      if (branding || school?.logo) {
        updateBranding({
          logo: school?.logo || null,
          app_name: branding?.app_name || null,
          primary_color: branding?.primary_color || null,
          accent_color: branding?.accent_color || null,
          favicon_url: branding?.favicon_url || null,
          login_bg_url: branding?.login_bg_url || null,
          hide_gradeverse_branding: branding?.hide_gradeverse_branding || false,
        });
      }
    },
    { immediate: true },
  );

  const school = computed(() => schoolResult.value?.getSchool);
  const academicCalendars = computed(() => schoolResult.value?.getSchool.academicCalendars || []);
  const classrooms = computed(() => schoolResult.value?.getSchool.classrooms || []);
  const school_sections = computed(() => schoolResult.value?.getSchool.sections || []);
  const classroomSections = computed(() => schoolResult.value?.getSchool.classroomSections || []);
  const paymentProviders = computed(() => schoolResult.value?.getSchool.paymentProviders || []);
  const activeAcademicCalendar = computed(() => {
    const calendars = schoolResult.value?.getSchool.academicCalendars || [];
    return calendars.find((calendar) => calendar?.is_active) || null;
  });

  return {
    school,
    loadingSchool,
    refetchSchool,
    errorSchool,
    academicCalendars,
    classrooms,
    school_sections,
    classroomSections,
    activeAcademicCalendar,
    paymentProviders,
    onResultSchool,
  };
};
